/** Токен хранится с признаком торговой сети и сессии SmartPricing: чужая запись отбрасывается */

import { MmSessionToken } from '../types/domain';
import { cancelScheduledRenewal, scheduleTokenRenewal } from './sessionRefresh';
import { getSpSessionFingerprint, hasSpAuthRecord, SP_AUTH_STORAGE_KEY } from './spSession';

const STORAGE_KEY = 'mm_session_token';
const STORAGE_VERSION = 1;

interface StoredRecord {
  v: number;
  accessToken: string;
  tradingNetworkId: number;
  obtainedAt: number;
  expiresInSeconds: number | null;
  /** null — запись не сохраняется в хранилище */
  spFingerprint: string | null;
}

let cached: StoredRecord | null = null;
let cacheLoaded = false;

/** true — запись прочитана из localStorage и могла остаться от другого пользователя */
let cachedIsFromStorage = false;

/** Активную сеть держит транспорт: иначе в окне переключения запрос уйдёт с токеном прежней сети */
let activeTradingNetworkId: number | null = null;

export function setActiveTradingNetworkId(tradingNetworkId: number | null): void {
  activeTradingNetworkId = tradingNetworkId;
}

export function getActiveTradingNetworkId(): number | null {
  return activeTradingNetworkId;
}

function readStorage(): StoredRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as StoredRecord;

    if (!parsed || parsed.v !== STORAGE_VERSION) {
      return null;
    }

    if (typeof parsed.accessToken !== 'string' || parsed.accessToken === '') {
      return null;
    }

    if (typeof parsed.tradingNetworkId !== 'number') {
      return null;
    }

    return parsed;
  } catch (e) {
    return null;
  }
}

function writeStorage(record: StoredRecord | null): void {
  try {
    if (record === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    }
  } catch (e) {
    // Приватный режим или переполненная квота: молча переходим на память
  }
}

function load(): StoredRecord | null {
  if (!cacheLoaded) {
    cached = readStorage();
    cacheLoaded = true;
    cachedIsFromStorage = cached !== null;
  }

  return cached;
}

/** Срок жизни здесь не проверяется: этим занимается sessionRefresh, иначе запись выбросит после F5 */
function isUsable(record: StoredRecord | null, tradingNetworkId: number): boolean {
  if (record === null) {
    return false;
  }

  if (record.tradingNetworkId !== tradingNetworkId) {
    return false;
  }

  if (!hasSpAuthRecord()) {
    // Без признаков входа в SmartPricing мобильный токен хранить нельзя
    return false;
  }

  const currentFingerprint = getSpSessionFingerprint();

  if (record.spFingerprint === null || currentFingerprint === null) {
    /** Без отпечатка доверяем только записи, созданной обменом в этой вкладке, иначе отдадим чужой токен */
    return !cachedIsFromStorage && record === cached;
  }

  return record.spFingerprint === currentFingerprint;
}

export function getStoredToken(tradingNetworkId: number): MmSessionToken | null {
  const record = load();

  /** Без записи входа в SmartPricing токен именно стираем: иначе он переживёт смену пользователя */
  if (record !== null && !hasSpAuthRecord()) {
    clearToken();

    return null;
  }

  if (!isUsable(record, tradingNetworkId)) {
    return null;
  }

  const usable = record as StoredRecord;

  return {
    accessToken: usable.accessToken,
    tradingNetworkId: usable.tradingNetworkId,
    obtainedAt: usable.obtainedAt,
    expiresInSeconds: usable.expiresInSeconds,
  };
}

/** При несовпадении активной сети токен не подставляем: лучше 401, чем чужие данные */
export function getRawAccessToken(): string | null {
  if (activeTradingNetworkId === null) {
    return null;
  }

  const token = getStoredToken(activeTradingNetworkId);

  return token === null ? null : token.accessToken;
}

/** Без вычислимого отпечатка токен живёт только в памяти вкладки и не сохраняется */
export function saveToken(token: MmSessionToken): void {
  const fingerprint = getSpSessionFingerprint();
  const record: StoredRecord = {
    v: STORAGE_VERSION,
    accessToken: token.accessToken,
    tradingNetworkId: token.tradingNetworkId,
    obtainedAt: token.obtainedAt,
    expiresInSeconds: token.expiresInSeconds,
    spFingerprint: fingerprint,
  };

  cached = record;
  cacheLoaded = true;
  cachedIsFromStorage = false;

  if (fingerprint !== null) {
    writeStorage(record);
  } else {
    writeStorage(null);
  }

  scheduleTokenRenewal(token.accessToken);
}

export function clearToken(): void {
  cancelScheduledRenewal();
  cached = null;
  cacheLoaded = true;
  cachedIsFromStorage = false;
  writeStorage(null);
}

export function invalidateTokenCache(): void {
  cached = null;
  cacheLoaded = false;
  cachedIsFromStorage = false;
}

/** Синхронизация между вкладками: выход из SmartPricing обязан выбросить и мобильный токен */
function installStorageListener(): void {
  if (typeof window === 'undefined' || !window.addEventListener) {
    return;
  }

  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) {
      invalidateTokenCache();

      return;
    }

    if (event.key === SP_AUTH_STORAGE_KEY && event.newValue === null) {
      // Выход из SmartPricing в соседней вкладке — чистим мобильный токен.
      clearToken();
    }
  });
}

installStorageListener();
